import {Box, Paper, Stack} from "@mui/material";
import TableUI from "../../components/table/TableUI";
import {headerData} from "./data/table-header-data";
import {useGetQuestionListQuery} from "../../store/api/problemsetApiSlice";
import {FilterType} from "../../config/enums";
import SearchUI from "../../components/search/SearchUI";
import {useExamPageNavigate} from "../../pages/exam/common/navigation/useExamPageNavigate";
import {ExamPageSection} from "../../pages/exam/common/navigation/routes";
import {SearchButtonDropdownType} from "../../components/search/search-button-dropdown/search-button-dropdown-main";
import {useGetExamConfigQuery} from "../../store/api/examConfigApiSlice";
import FilterButtonUI from "./components/filter/common/FilterButtonUI";
import SelectedFiltersRow from "./components/filter/filter-chips/SelectedFiltersRowUI";
import {HandleProblemsetFilterUpdatesProps} from "../../hooks/useProblemsetSync";
import {forwardRef, useImperativeHandle} from "react";

export interface ProblemsetTableV2Ref {
  getQuestionIds: () => {qId: string}[];
  isNextPageAvailable: () => boolean;
  isPreviousPageAvailable: () => boolean;
}
interface ProblemsetTableV2Props {
  subjectId?: string;
  chapterId?: string;
  [FilterType.ATTEMPT_STATUS]?: string[];
  [FilterType.PATTERN]?: string[];
  [FilterType.DIFFICULTY]?: string[];
  [FilterType.FEATURE]?: string;
  [FilterType.PAGE]?: number;
  handleFiltersUpdates: (props: HandleProblemsetFilterUpdatesProps) => void;
}
export default function ProblemsetTableV2(props: ProblemsetTableV2Props) {
  const navigate = useExamPageNavigate();

  const {data, isFetching} = useGetQuestionListQuery(
    {
      subjects: props.subjectId ? [props.subjectId] : [],
      chapters: props.chapterId ? [props.chapterId] : [],
      attemptStatus: props[FilterType.ATTEMPT_STATUS] ?? [],
      patterns: props[FilterType.PATTERN] ?? [],
      difficulty: props[FilterType.DIFFICULTY] ?? [],
      page: props[FilterType.PAGE] ? Number(props[FilterType.PAGE]) : 1,
      feature: props[FilterType.FEATURE] ?? undefined,
    },
    {
      skip: !(!!props.subjectId && !!props.chapterId),
    },
  );

  const {data: examConfigData} = useGetExamConfigQuery();
  const subjects = examConfigData?.subjects ?? [];
  const selectedSubject = subjects.find(
    subject => subject.id === props.subjectId,
  );
  const chapters = examConfigData?.chapters[selectedSubject?.id ?? ""] ?? [];
  const selectedChapter = chapters.find(
    chapter => chapter.id === props.chapterId,
  );
  const difficulty = examConfigData?.difficulty;
  const selectedDifficulty = difficulty?.filter(d =>
    props[FilterType.DIFFICULTY]?.includes(d.id),
  );
  const patterns = examConfigData?.patterns;
  const selectedPatterns = patterns?.filter(p =>
    props[FilterType.PATTERN]?.includes(p.id),
  );
  const attemptStatus = examConfigData?.attemptStatus;
  const selectedAttemptStatus = attemptStatus?.filter(a =>
    props[FilterType.ATTEMPT_STATUS]?.includes(a.id),
  );

  const tableData = {
    isLoading: isFetching,
    headerData: headerData,
    rowsData: data?.rowsData ?? null,
    pagination: {
      previous: data?.pagination?.previous ?? null,
      next: data?.pagination?.next ?? null,
      onPageChange: (newPage: number) => {
        props.handleFiltersUpdates({
          [FilterType.PAGE]: newPage,
        });
      },
    },
    emptyStateViewModel: {
      title: "No questions found",
      description: "Try changing the filters or search for a question.",
    },
    onRowClick: (id: string) => {
      navigate(ExamPageSection.PROBLEM_SET_QUESTION, {
        questionId: id,
      });
    },
  };

  const onFiltersUpdate = (
    filterType:
      | FilterType.ATTEMPT_STATUS
      | FilterType.DIFFICULTY
      | FilterType.PATTERN,
    selectedFilters: string[],
  ) => {
    props.handleFiltersUpdates({
      [filterType]: selectedFilters,
    });
  };

  return (
    <Paper variant="feature" sx={{p: "20px", minHeight: "892px"}}>
      <Box sx={{height: 4}} />
      <Stack direction="row" justifyContent="space-between">
        <SearchUI
          searchData={{
            buttonLabelViewModel: selectedChapter
              ? {
                  name: selectedChapter.viewModel.name,
                  id: selectedChapter.id,
                  icon: selectedChapter.viewModel.icon ?? "auto_stories",
                  color: selectedChapter.viewModel.color,
                }
              : undefined,
            dropdownType: SearchButtonDropdownType.SYLLABUS,
            hasAnimation: true,
          }}
          placeholder={`Search for questions`}
        />
        <Box flexGrow={1} />
        <FilterButtonUI />
      </Stack>

      <Box sx={{height: 8}} />

      <SelectedFiltersRow
        filters={{
          [FilterType.DIFFICULTY]: selectedDifficulty ?? [],
          [FilterType.PATTERN]: selectedPatterns ?? [],
          [FilterType.ATTEMPT_STATUS]: selectedAttemptStatus ?? [],
        }}
        onFitersUpdate={onFiltersUpdate}
      />

      <Box sx={{height: 8}} />
      <TableUI {...tableData} />
    </Paper>
  );
}
